import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface HomeCityCafe {
  name: string;
  /** The café's real work readiness score, or null if it has none. */
  score: number | null;
}

export interface HomeCityCoverage {
  slug: string;
  displayName: string;
  /** A few real, currently-scored cafés in that city. */
  cafes: HomeCityCafe[];
}

/** Cafés shown per city on the homepage. */
export const NAMES_PER_CITY = 3;

/** How many of a city's top scorers the selection rule draws from. */
export const POOL_PER_CITY = 25;

/**
 * Choose the three cafés a city card shows.
 *
 * Taking the top three sorted by score gave a wall of 100s: real numbers that
 * read as invented, in the section that exists to prove the score discriminates.
 *
 * The rule: inside the city's top scorers (`pool`, already ordered by score
 * descending then name ascending), find the distinct scores present and take the
 * highest, the median and the lowest of those. For each, show the alphabetically
 * first café holding it. Every pick is therefore one of the city's best rated
 * cafés, the three numbers differ whenever the city has three different numbers,
 * and the same three come back on every load because nothing here samples or
 * randomises.
 *
 * Nothing is fabricated or rounded to make the spread: if a city only has one or
 * two distinct scores, that is what it shows.
 */
export function pickCityCafes(pool: HomeCityCafe[]): HomeCityCafe[] {
  const scored = pool.filter((c) => c.score !== null);
  if (scored.length <= NAMES_PER_CITY) return pool.slice(0, NAMES_PER_CITY);

  const distinct = [...new Set(scored.map((c) => c.score as number))].sort((a, b) => b - a);
  const wanted =
    distinct.length >= NAMES_PER_CITY
      ? [distinct[0], distinct[(distinct.length - 1) >> 1], distinct[distinct.length - 1]]
      : distinct;

  const picked = wanted.map((score) => scored.find((c) => c.score === score) as HomeCityCafe);
  // Only when the city genuinely has fewer than three distinct scores.
  for (const c of scored) {
    if (picked.length >= NAMES_PER_CITY) break;
    if (!picked.includes(c)) picked.push(c);
  }
  return picked.slice(0, NAMES_PER_CITY);
}

/**
 * The published /work-cafes/* city pages, each with a few real café names.
 *
 * The city pages are the strongest asset on the site and the homepage was
 * reducing them to 12px chips. This gives the coverage section something true to
 * show: named cafés that are live and scored right now, read from the same
 * tables the city pages read.
 *
 * Two reads, not one per city: the page list, then one pass over cafés in those
 * cities. `useCityWorkPage` issues five queries per city, which is right for a
 * city page and far too heavy for a landing page.
 *
 * Names come from the top-scored active cafés per city, so they can differ from a
 * city page's curated running order. They are always real, live and in that city.
 * Which three of them a card shows is `pickCityCafes` below.
 */
export function useHomeCityCoverage() {
  return useQuery({
    queryKey: ["home-city-coverage"],
    staleTime: 60 * 60 * 1000,
    retry: 1,
    queryFn: async (): Promise<HomeCityCoverage[]> => {
      const { data: pages, error: pagesErr } = await supabase
        .from("seo_city_pages")
        .select("slug, display_name, city_match, match_type")
        .eq("is_published", true)
        .eq("match_type", "city")
        .order("sort_order", { ascending: true });
      if (pagesErr) throw pagesErr;
      if (!pages?.length) return [];

      const cities = [...new Set(pages.map((p) => p.city_match).filter(Boolean))] as string[];
      if (!cities.length) return [];

      const { data: cafes, error: cafesErr } = await supabase
        .from("cafes")
        .select("name, city, work_readiness_score")
        .in("city", cities)
        .eq("is_active", true)
        .gt("work_readiness_score", 0)
        .order("work_readiness_score", { ascending: false })
        .order("name", { ascending: true })
        .limit(cities.length * POOL_PER_CITY);
      if (cafesErr) throw cafesErr;

      const byCity = new Map<string, HomeCityCafe[]>();
      for (const c of cafes ?? []) {
        if (!c.city || !c.name) continue;
        const list = byCity.get(c.city) ?? [];
        if (list.length < POOL_PER_CITY) {
          // Real score only. A café without one shows its name and no number,
          // rather than a placeholder that would read as fact.
          const raw = (c as { work_readiness_score?: number | null }).work_readiness_score;
          list.push({ name: c.name, score: typeof raw === "number" ? raw : null });
        }
        byCity.set(c.city, list);
      }

      return pages
        .map((p) => ({
          slug: p.slug as string,
          displayName: p.display_name as string,
          cafes: pickCityCafes(byCity.get(p.city_match as string) ?? []),
        }))
        .filter((c) => c.cafes.length > 0);
    },
  });
}
